/* eslint-disable no-underscore-dangle */
import { actionSetRetailerDetail } from '@lib/core/retailers/slices/retailer';
import { actionSetRetailerLocationDetail } from '@lib/core/retailers/slices/retailerLocation';
import { ERROR_UNRECOGNIZED_POST_MESSAGE_EVENT, isApplicationPmi } from '@lib/core/service/consts';
import { selectServiceProductCategory } from '@lib/core/service/selectors';
import { selectPageScrollValue } from '@lib/core/service/selectors/widgetInstance';
import { setProductCategory, setServiceLocale } from '@lib/core/service/slices';
import {
  setPageScrollValue,
  setProductInteraction,
  setTasteMatchProductId,
  setWidgetIframeParentDimension,
} from '@lib/core/service/slices/widgetInstanceSlice';
import { store } from '@lib/core/service/store';
import { isAppInIframe, prependBasename } from '@lib/core/service/utils';
import { history } from '@lib/core/service/utils/Navigator';
import { IsLauncherOpen, SkipLauncherAutoOpen } from '@lib/core/service/utils/widgetLauncherSessionState';
import { actionSetUserProfileId } from '@lib/core/users/slices/profile';
import { HOST_CMS_EVENT_TYPES, HOST_LINK_STORAGE_KEY, POST_MESSAGES } from '@lib/tools/comms/consts';
import { THostCmsEvent, TNavigateWidgetIframe } from '@lib/tools/comms/interfaces';
import {
  actionSetCommsHandshake,
  actionSetCommsHostCustomId,
  actionSetCommsHostRedirectUrl,
  actionSetCommsHostRedirectUrlLevia,
} from '@lib/tools/comms/slices';
import { UTM_SOURCE } from '@lib/tools/dat/consts';
import MixpanelTracker from '@lib/tools/dat/mixpanel';

/**
 * Send payloads back to hosting libraries for PMI.
 */
export const sendPostMessage = (payload?: Record<string, any>) => {
  if (isAppInIframe) {
    if (
      payload[POST_MESSAGES.ADD_TO_CART] ||
      payload[POST_MESSAGES.QUIZ_COMPLETED] ||
      payload[POST_MESSAGES.PRODUCT_SETS] ||
      payload[POST_MESSAGES.BEST_MATCH] ||
      payload[POST_MESSAGES.SEND_TA_GA_EVENT] ||
      payload[POST_MESSAGES.DOM_HEIGHT] ||
      payload[POST_MESSAGES.REQUEST_HOST_HANDSHAKE] ||
      payload[POST_MESSAGES.NAVIGATE_WIDGET_IFRAME]
    ) {
      window.parent.postMessage(payload, '*');
    }
  }
};

export const syncAuthToHost = ({
  accessToken,
  profileId,
  refreshToken,
}: {
  accessToken?: string;
  profileId?: string;
  refreshToken?: string;
}) => {
  if (!isAppInIframe || !accessToken || !refreshToken) return;

  window.parent.postMessage(
    {
      payload: {
        auth: {
          access_token: accessToken,
          refresh_token: refreshToken,
        },
        profileId,
      },
      type: POST_MESSAGES.SYNC_AUTH,
    },
    '*',
  );
};

export const clearHostSyncedAuth = () => {
  if (!isAppInIframe) return;
  window.parent.postMessage({ type: POST_MESSAGES.SYNC_AUTH_CLEAR }, '*');
};

export const initComms = dispatch => {
  // Prevent double init in dev/runtime: only one message listener and handshake.
  const g: any = globalThis as any;
  if (g.__vhCommsListenerInitialized || store.getState()?.comms?.isHostHandshakeApproved) return;
  g.__vhCommsListenerInitialized = true;

  const handleHostMessage = (event: MessageEvent) => {
    if (event.data?.source?.includes('@devtools')) return;

    if (event.origin) {
      switch (event.data.type) {
        case POST_MESSAGES.HANDSHAKE:
          dispatch(actionSetCommsHandshake(true));

          if (isApplicationPmi) {
            // ! Obtain the RTL and RT for the hosting lib and set them in the application state
            if (event.data?.payload?.retailer) {
              dispatch(actionSetRetailerDetail(event.data?.payload.retailer)).then(() => {
                if (event.data?.payload?.retailerLocation) {
                  const retailerLocation = event.data?.payload.retailerLocation;
                  const {
                    product_categories: productCategoriesRaw,
                    design_sets: designSetsRaw,
                    addons: addonsRaw,
                  } = retailerLocation;

                  const productCategories = [];
                  productCategoriesRaw.forEach(productCategory => productCategories.push(productCategory.name));
                  const addons = [];
                  addonsRaw.forEach(addon => addons.push(addon.name));

                  const preparedData = retailerLocation;
                  if (preparedData.design_sets?.length) {
                    const assignedDesignSets = [];
                    const allowedDesignSets = {};
                    let currentAssignedDesignSets = [];

                    designSetsRaw.forEach(designSet => {
                      const designSetProductCategories = designSet.product_categories;
                      designSetProductCategories.forEach(product => {
                        assignedDesignSets.push({ [product.name]: designSet.name });
                      });
                    });

                    let productDesignSets = [];

                    if (isApplicationPmi) {
                      productCategories.forEach(product => {
                        productDesignSets = assignedDesignSets.filter(ds => Object.keys(ds)[0] === product);

                        currentAssignedDesignSets.push(...productDesignSets);
                      });
                    } else {
                      currentAssignedDesignSets = assignedDesignSets;
                    }

                    currentAssignedDesignSets.forEach(designSet => {
                      productCategories.forEach(product => {
                        if (designSet[product]) allowedDesignSets[product] = designSet[product];
                      });
                    });

                    preparedData.design_sets = allowedDesignSets;
                    preparedData.product_categories = productCategories;
                    preparedData.addons = addons;
                  }

                  dispatch(actionSetRetailerLocationDetail(event.data?.payload.retailerLocation)).then(() => {
                    if (event.data?.payload.productCategory) {
                      dispatch(setProductCategory(event.data?.payload.productCategory));
                    }

                    if (event.data?.payload?.profileId) {
                      dispatch(actionSetUserProfileId(event.data?.payload.profileId));
                    }
                  });
                }
              });
            }
          }

          if (event.data?.payload?.catalogRedirectUrl) {
            dispatch(actionSetCommsHostRedirectUrl(event.data?.payload.catalogRedirectUrl));
          }

          if (event.data?.payload?.catalogRedirectUrlLevia) {
            dispatch(actionSetCommsHostRedirectUrlLevia(event.data?.payload.catalogRedirectUrlLevia));
          }

          if (event.data?.payload?.customId) {
            dispatch(actionSetCommsHostCustomId(event.data?.payload.customId));
          }
          break;
        case POST_MESSAGES.LOCALE:
          dispatch(setServiceLocale(event.data.payload));
          break;
        case POST_MESSAGES.PRODUCT_CATEGORY: {
          const requestedProductCategory = event.data.payload;
          const currentProductCategory = selectServiceProductCategory(store.getState());

          if (currentProductCategory !== requestedProductCategory) {
            dispatch(setProductCategory(event.data.payload));
          }
          break;
        }
        case POST_MESSAGES.REQUEST_DOM_HEIGHT: {
          const frameRoot = document.getElementById('root');
          if (frameRoot) {
            // * Account for sticky footer height on mobile
            const footerElement = document.getElementsByClassName('ta-v2-quiz-footer-wrapper')?.[0];

            if (footerElement) {
              footerElement.setAttribute('style', 'position: relative;');
              const footerElementPos = footerElement.getBoundingClientRect();
              // Add an offset for some extra height.
              sendPostMessage({
                [POST_MESSAGES.DOM_HEIGHT]: footerElementPos.y + footerElementPos.height,
              });
            } else {
              // Add an offset for some extra height.
              sendPostMessage({
                [POST_MESSAGES.DOM_HEIGHT]: frameRoot.offsetHeight,
              });
            }
          }
          break;
        }
        case POST_MESSAGES.DISPLAY_ROUTE:
          if (window.location?.pathname?.includes(event.data.payload)) {
            break;
          }
          // Host open/close syncs routes into the iframe. Must replace — a push would
          // sit in Chrome joint session history ahead of the host page (Back stays in SPA).
          history.push(prependBasename(event.data.payload), { replace: true });
          break;
        case POST_MESSAGES.HOST_CMS_EVENT: {
          console.info('CM: event received', event.data.payload);

          const { payload, eventType } = event.data.payload as THostCmsEvent;

          if (eventType === HOST_CMS_EVENT_TYPES.PRODUCT_VIEW) {
            dispatch(setTasteMatchProductId(payload.properties.productId));
            break;
          }

          if (eventType === HOST_CMS_EVENT_TYPES.TRACKING_EVENT) {
            MixpanelTracker.events.experienceHostCmsEvent(payload.name, payload.properties);
            break;
          }

          break;
        }
        case POST_MESSAGES.VH_HOST_LINK: {
          const payload = event.data?.payload?.payload;

          if (!payload) break;

          sessionStorage.setItem(HOST_LINK_STORAGE_KEY, payload);

          break;
        }
        case POST_MESSAGES.PAGE_SCROLL: {
          const newScrollAction = event.data.payload;

          if (!newScrollAction) break;

          const currentScrollState = selectPageScrollValue(store.getState());

          if (newScrollAction !== currentScrollState) {
            dispatch(
              setPageScrollValue({
                scrollAction: newScrollAction,
              }),
            );
          }
          break;
        }
        case POST_MESSAGES.VH_WIDGET_CLOSED:
          IsLauncherOpen.set(true);

          if (event.data.payload) {
            dispatch(setProductInteraction({ timer: new Date().getTime() + 1 * 60 * 1000 }));
          }

          break;
        case POST_MESSAGES.LAUNCHER_INITIAL_CLOSED:
          // First mobile load after userJourneyStartDelay — launcher starts closed; same skip-auto-open as VH_WIDGET_CLOSED.
          SkipLauncherAutoOpen.set();
          IsLauncherOpen.set(false);
          break;
        case POST_MESSAGES.IFRAME_PARENT_DIMENSION:
          dispatch(setWidgetIframeParentDimension(event.data.payload));
          break;
        default:
          // Ignore webpack events
          if (!event.data?.source?.includes('devtools')) {
            console.warn(ERROR_UNRECOGNIZED_POST_MESSAGE_EVENT, event.data);
          }
          break;
      }
    }
  };

  /**
   * Establish a handshake with hosting libraries. This prevents direct injection of our apps into iframes.
   * @todo implement host handshake on vinhood library
   */
  window.addEventListener('message', handleHostMessage);

  sendPostMessage({
    [POST_MESSAGES.REQUEST_HOST_HANDSHAKE]: true,
  });
};

export const broadcastQuizCompletionToHost = (characterIdentifier: string, characterName: string) => {
  const postMessage = {};
  postMessage[POST_MESSAGES.QUIZ_COMPLETED] = { identifier: characterIdentifier, name: characterName };
  sendPostMessage(postMessage);
};

export const isFromSharedLink = new URLSearchParams(window.location.search).get('utm_source') === UTM_SOURCE;

export const addToEcommerceCart = (ecommerceId: string, quantity: number, optionId?: string, variantId?: string) => {
  const postMessage = {};
  postMessage[POST_MESSAGES.ADD_TO_CART] = {
    ecommerceID: ecommerceId,
    optionID: optionId,
    quantity,
    variantID: variantId,
  };

  sendPostMessage(postMessage);
};

export const navigateWidgetIframe = (payload: TNavigateWidgetIframe) =>
  sendPostMessage({
    [POST_MESSAGES.NAVIGATE_WIDGET_IFRAME]: payload,
  });
